/*
 * Persisted chat-session state for the embedded chat widgets.
 *
 * Every embedded widget runs inside its own iframe on the customer's site, so
 * each host-page navigation unloads and remounts it from scratch. Without a
 * durable record of the conversation, walking from one WordPress page to the
 * next looked to the visitor like the chat had ended: the launcher came back on
 * the channel picker with an empty transcript, and the next message opened a
 * brand-new backend session.
 *
 * Everything a remount needs to pick the same conversation back up lives here -
 * the backend session id, the message history, the pre-chat form answers, and
 * the launcher's own open/started state. All of it is keyed per agent and
 * scoped to a resume window: a visitor who comes back inside that window
 * continues the same conversation, one who comes back later starts clean.
 *
 * Used by ChatEmbedWidget (transcript + session id), ChatEmbedButton and
 * ComboWidgetV2 (launcher state). Those three must agree on the keys and on the
 * resume verdict, which is why all of it is centralized in this module.
 */
import api from '../api';

// How long a conversation stays resumable after the visitor's last activity.
// Deliberately under the backend's 30-minute stale-chat cutoff (the auto-close
// sweep in routes/sessions.py) so the widget never resumes a session the server
// has just finalized - that would show the old transcript continuing into a
// fresh backend session and split the recorded conversation in two.
export const RESUME_WINDOW_MS = 25 * 60 * 1000;

const storageKeys = (agentId) => ({
  history: `chat_history_${agentId}`,
  session: `chat_session_id_${agentId}`,
  meta: `chat_session_meta_${agentId}`,
});

// Safari, and any browser with third-party storage blocked, throws on storage
// access from inside an embedded iframe. Falling back to a per-page memory shim
// keeps the widget fully working within a single page instead of breaking on
// mount; it just cannot carry a conversation across a navigation there.
//
// The shim is only consulted when real storage is unusable, never as a backstop
// for a key that reads back empty: a value that was deliberately cleared - here
// or in another tab - has to stay cleared, or ending a chat in one place would
// resurrect it in another.
const memoryFallback = {};
let storageUsable = null;

const isBrowser = () => typeof window !== 'undefined';

const canUseStorage = () => {
  if (storageUsable !== null) return storageUsable;
  try {
    const probe = '__braincx_chat_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    storageUsable = true;
  } catch {
    storageUsable = false;
  }
  return storageUsable;
};

const readRaw = (key) => {
  if (!canUseStorage()) {
    return Object.prototype.hasOwnProperty.call(memoryFallback, key) ? memoryFallback[key] : null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key, value) => {
  if (!canUseStorage()) {
    memoryFallback[key] = value;
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Usable but full, or blocked mid-session. Keep the conversation alive for
    // this page at least.
    memoryFallback[key] = value;
  }
};

const removeRaw = (key) => {
  delete memoryFallback[key];
  try {
    window.localStorage.removeItem(key);
  } catch {}
};

// ── Session id ───────────────────────────────────────────────────────────────

export const readSessionId = (agentId) => {
  if (!isBrowser() || !agentId) return null;
  return readRaw(storageKeys(agentId).session) || null;
};

export const writeSessionId = (agentId, sessionId) => {
  if (!isBrowser() || !agentId || !sessionId) return;
  writeRaw(storageKeys(agentId).session, sessionId);
};

// ── Transcript ───────────────────────────────────────────────────────────────

export const readMessages = (agentId) => {
  if (!isBrowser() || !agentId) return [];
  try {
    const saved = readRaw(storageKeys(agentId).history);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((msg) => ({ ...msg, timestamp: new Date(msg.timestamp) }));
  } catch (err) {
    console.error('Failed to read chat history:', err);
    return [];
  }
};

export const writeMessages = (agentId, messages) => {
  if (!isBrowser() || !agentId) return;
  try {
    writeRaw(
      storageKeys(agentId).history,
      JSON.stringify(
        messages.map((msg) => ({
          ...msg,
          timestamp: (msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp)).toISOString(),
        }))
      )
    );
  } catch (err) {
    console.error('Failed to save chat history:', err);
  }
};

// ── Meta (activity clock, form answers, launcher state) ──────────────────────

export const readMeta = (agentId) => {
  if (!isBrowser() || !agentId) return {};
  try {
    const saved = readRaw(storageKeys(agentId).meta);
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const patchMeta = (agentId, patch) => {
  if (!isBrowser() || !agentId) return {};
  const next = { ...readMeta(agentId), ...patch };
  try {
    writeRaw(storageKeys(agentId).meta, JSON.stringify(next));
  } catch {}
  return next;
};

/** Push the activity clock forward, keeping the conversation inside the resume window. */
export const noteActivity = (agentId) => patchMeta(agentId, { lastActivityAt: Date.now(), leftAt: null });

/**
 * Record that the page is going away. This replaces ending the backend session
 * on unload: from inside the iframe a same-site navigation and a tab close look
 * identical, and ending the session on both is what killed the chat on every
 * page change. The timestamp lets the next mount decide which one it was - a
 * quick remount resumes, a long gap expires the session (see resolveSession).
 */
export const noteLeft = (agentId) => patchMeta(agentId, { leftAt: Date.now() });

export const clearAll = (agentId) => {
  if (!isBrowser() || !agentId) return;
  const keys = storageKeys(agentId);
  removeRaw(keys.history);
  removeRaw(keys.session);
  removeRaw(keys.meta);
};

// ── Resume verdict ───────────────────────────────────────────────────────────

// One verdict per agent per page load. The launcher and the chat widget both
// ask and must get the same answer, and the abandoned-session end call below
// must fire at most once.
const verdicts = new Map();

// The resume window is measured from real conversation activity - a message
// sent, or the pre-chat form answered - and deliberately not from page views or
// unloads. Browsing the site with the widget closed must not keep an abandoned
// conversation alive forever, because the backend's own staleness check counts
// from the last message too; letting the two drift apart is what would show one
// transcript spanning two recorded sessions. leftAt is only a fallback for the
// odd case of a session id with no recorded activity behind it.
const lastSeenAt = (meta) => meta.lastActivityAt || meta.leftAt || 0;

const endAbandonedSession = (agentId, sessionId, seenAt) => {
  if (!sessionId) return;
  const endedAt = seenAt ? new Date(seenAt).toISOString() : null;
  const url =
    `/agents/${agentId}/chat/embed/end?session_id=${encodeURIComponent(sessionId)}` +
    (endedAt ? `&ended_at=${encodeURIComponent(endedAt)}` : '');
  // Fire and forget, and never let it surface: the callers resolve the verdict
  // while rendering, so a throw here would take the whole widget down over a
  // bookkeeping call. The backend's stale-chat sweep closes out anything this
  // misses.
  try {
    Promise.resolve(api.post(url)).catch(() => {});
  } catch {}
};

/**
 * Decide whether the stored conversation is still the visitor's live one.
 *
 * Resumable: the caller restores the session id, transcript, form answers and
 * launcher state as they were.
 *
 * Expired: storage is wiped and the abandoned backend session is closed out
 * here, dated to when the visitor actually left rather than to now, so an idle
 * gap is not billed as conversation time. (The backend's stale-chat sweep is the
 * backstop for a visitor who never comes back at all.)
 */
export const resolveSession = (agentId) => {
  if (!isBrowser() || !agentId) return { resumable: false, meta: {} };
  if (verdicts.has(agentId)) return verdicts.get(agentId);

  const meta = readMeta(agentId);
  const sessionId = readSessionId(agentId);
  const seenAt = lastSeenAt(meta);

  // No activity clock at all means the stored state predates this mechanism (or
  // only the transcript survived). Treat that as resumable rather than throwing
  // away a conversation the visitor may still be in the middle of.
  const resumable = !sessionId || !seenAt || Date.now() - seenAt <= RESUME_WINDOW_MS;

  if (!resumable) {
    endAbandonedSession(agentId, sessionId, seenAt);
    clearAll(agentId);
  }

  const verdict = resumable ? { resumable: true, meta } : { resumable: false, meta: {} };
  verdicts.set(agentId, verdict);
  return verdict;
};

/** Forget the cached verdict so a deliberate reset (End Chat / Clear) starts clean. */
export const resetVerdict = (agentId) => {
  verdicts.delete(agentId);
};

/** True when the stored transcript holds a real exchange, not just a greeting. */
export const hasConversation = (agentId) => readMessages(agentId).some((msg) => msg.role === 'user');

/**
 * Remember the launcher's own state (open, started, which channel) so a remount
 * puts the visitor back where they were instead of on the closed launcher or the
 * channel picker. Note that this never touches the activity clock: a visitor
 * walking around the site with the widget closed is not chat activity, and
 * treating it as such would hold an abandoned session open past the point the
 * backend considers it stale.
 */
export const saveLauncherState = (agentId, state) => patchMeta(agentId, state);

/**
 * What the launcher should come back as.
 *
 * A conversation is only carried across a navigation once the visitor has
 * actually said something - an unanswered greeting is not worth reopening the
 * panel over. Voice is never restored as the live channel: a call cannot
 * survive the page unload that killed its WebRTC connection, so a visitor who
 * was on the voice tab comes back able to place a new call rather than staring
 * at a dead one.
 */
export const restoreLauncherState = (agentId) => {
  const { resumable, meta } = resolveSession(agentId);
  const inConversation = resumable && Boolean(meta.hasStarted) && hasConversation(agentId);
  return {
    inConversation,
    isOpen: inConversation && Boolean(meta.isOpen),
    hasStarted: inConversation,
    channel: inConversation ? 'chat' : null,
  };
};
